/**
 * PATH: packages/email-hub/src/feature/components/email-reader/ReaderSender.tsx
 * FILE_ID: EMAILHUB-EMAILREADER-SENDER-001
 * PURPOSE: Render sender addressing followed by the canonical metadata header.
 */
import { EMAIL_ADDRESS_ATTR, RecipientList } from '@quotech/ui';
import { cn } from '../../../_internal/lib/utils';
import { formatFullDate } from '../../lib/utils';
import { ReceivingMailboxVia } from '../RowAttributionChips';
import { WorkflowRoleChip } from '../WorkflowRoleChip';
import { ReaderSignalStrip } from '../../signal-strip';
import type { EmailReaderController } from './useEmailReaderController';

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-pink-500',
  'bg-indigo-500',
];

function getAvatarColor(name: string) {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) {
    hash = name.charCodeAt(index) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function ReaderSender({ controller }: { controller: EmailReaderController }) {
  const {
    email,
    hideJobLink,
    hideSenderIdentity,
    refetch,
    allocationMembers,
    canRequestRule,
    kanbanApi,
    kanbanOrgId,
    openAllocatedBoard,
    openAllocatedCard,
    setRuleDialogOpen,
    setPeekJob,
  } = controller;
  if (!email) return null;
  const senderName = email.from_name || email.from_address.split('@')[0];

  return (
    <>
      <div className="mb-3 flex items-start gap-4">
        <div
          className={cn(
            'h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold shrink-0',
            getAvatarColor(email.from_name || email.from_address)
          )}
        >
          {getInitials(senderName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-semibold text-foreground"
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
            >
              {senderName}
            </span>
            <span
              className="text-sm text-muted-foreground select-text"
              {...(email.from_address ? { [EMAIL_ADDRESS_ATTR]: email.from_address } : {})}
            >
              &lt;{email.from_address}&gt;
            </span>
            <WorkflowRoleChip email={email} />
          </div>
          <RecipientList
            label="To"
            recipients={email.to_addresses}
            emptyText="me"
            className="block text-sm text-muted-foreground"
          />
          <ReceivingMailboxVia email={email} />
          {!!email.cc_addresses?.length && (
            <RecipientList
              label="Cc"
              recipients={email.cc_addresses}
              className="block text-sm text-muted-foreground"
            />
          )}
          {!!email.bcc_addresses?.length && (
            <RecipientList
              label="Bcc"
              recipients={email.bcc_addresses}
              className="block text-sm text-muted-foreground"
            />
          )}
          <div className="text-sm text-muted-foreground/70">
            {formatFullDate(email.received_at)}
          </div>
        </div>
      </div>
      <ReaderSignalStrip
        email={email}
        api={kanbanApi}
        orgId={kanbanOrgId}
        onRefetch={refetch}
        onOpenCard={openAllocatedCard}
        onOpenBoard={openAllocatedBoard}
        onPeekJob={setPeekJob}
        canRequestRule={canRequestRule}
        onOpenRuleDialog={() => setRuleDialogOpen(true)}
        allocationMembers={allocationMembers}
        hideSenderIdentity={hideSenderIdentity}
        hideJobLink={hideJobLink}
      />
    </>
  );
}
